import React, { useCallback, useState } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { IconButton } from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import ChevronLeft from '@atlaskit/icon/core/chevron-left';
import CrossIcon from '@atlaskit/icon/core/cross';
import { type SmartCardProvider as DefaultSmartCardProvider } from '@atlaskit/link-provider';
import Lozenge from '@atlaskit/lozenge';
import AkModalDialog, { ModalBody, ModalHeader } from '@atlaskit/modal-dialog';
import { Flex, Text } from '@atlaskit/primitives/compiled';
import { type ConversationAssistantConfig } from '@atlassian/conversation-assistant';

import messages from './messages';
import { SmartCreatePrompt } from './prompt';
import { StagingArea } from './staging-area';
import { FinalizeActionButton } from './staging-area/finalize-action-button';
import { SmartCreateIcon } from './staging-area/smart-create-icon';

type ErrorBoundaryProps = {
	children: React.ReactNode;
};

type SmartCreationModalProps = {
	spaceKey: string;
	config: ConversationAssistantConfig;
	handleClose: () => void;
	handlePrevious?: () => void;
	handleNext?: () => void;
	contentId: string;
	SmartCardProvider?: typeof DefaultSmartCardProvider;
	ErrorBoundary?: React.ComponentType<ErrorBoundaryProps>;
};

const styles = cssMap({
	closeButton: {
		width: '100%',
		justifyContent: 'flex-end',
	},
});

export const SmartCreationModal = ({
	spaceKey,
	config,
	handleClose,
	handlePrevious,
	handleNext,
	contentId,
	SmartCardProvider,
	ErrorBoundary,
}: SmartCreationModalProps) => {
	const [showStagingArea, setShowStagingArea] = useState<boolean>(false);

	const handleNextStage = useCallback(() => {
		setShowStagingArea(true);
		handleNext?.();
	}, [handleNext]);

	const handleBackStage = useCallback(() => {
		setShowStagingArea(false);
		handlePrevious?.();
	}, [handlePrevious]);

	return (
		<AkModalDialog width="100%" height="100%">
			<ModalHeader>
				{showStagingArea && (
					<Flex alignItems="center" gap="space.100">
						<IconButton
							icon={ChevronLeft}
							label="Go back"
							shape="circle"
							onClick={handleBackStage}
						/>
						<SmartCreateIcon contentType="livedoc" />
						<Text size="medium">Page Title</Text>
						<Lozenge appearance="default">
							<FormattedMessage {...messages.previewLozenge} />
						</Lozenge>
					</Flex>
				)}
				<Flex alignItems="center" gap="space.200" xcss={!showStagingArea && styles.closeButton}>
					{showStagingArea && <FinalizeActionButton />}
					<IconButton icon={CrossIcon} label="Close" shape="circle" onClick={handleClose} />
				</Flex>
			</ModalHeader>
			<ModalBody>
				{!showStagingArea ? (
					<SmartCreatePrompt
						spaceKey={spaceKey}
						onNavigate={handleNextStage}
						SmartCardProvider={SmartCardProvider}
						ErrorBoundary={ErrorBoundary}
					/>
				) : (
					<StagingArea config={config} contentId={contentId} spaceKey={spaceKey} />
				)}
			</ModalBody>
		</AkModalDialog>
	);
};
